import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Users, Shield, AlertCircle, Lock, Check, ChevronRight, ChevronLeft, UserPlus, Shuffle } from 'lucide-react';
import './BRTeamJoinModal.css';

const BRTeamJoinModal = ({ tournament, user, onJoin, onClose, loading, t }) => {
  // Get tournament-level team settings
  const tournamentTeamMode = tournament.brTeamSettings?.mode || 'random';
  const creatorSquadmates = tournament.brTeamSettings?.creatorSquadmates || [];

  // Flow: 1 = team selection, 2 = preference selection (only for empty teams)
  const [step, setStep] = useState(1);
  const [selectedTeamNumber, setSelectedTeamNumber] = useState(null);
  const [selectedTeamIsEmpty, setSelectedTeamIsEmpty] = useState(false);

  // Initialize state based on tournament settings
  const getInitialPreference = () => {
    if (tournamentTeamMode === 'solo') return 'solo';
    return 'random';
  };

  const [teamPreference, setTeamPreference] = useState(getInitialPreference());
  const [squadmates, setSquadmates] = useState(['', '', '']);

  const isBRDuo = tournament.multiplayerType === 'duo';
  const maxSquadmates = isBRDuo ? 1 : 3;
  const maxTeamSize = isBRDuo ? 2 : 4;

  // Calculate available teams
  const teams = useMemo(() => {
    const teamCount = isBRDuo
      ? Math.ceil(tournament.maxPlayers / 2)
      : Math.ceil(tournament.maxPlayers / 4);

    const teamsArray = [];

    const getUserIdString = (assignment) => {
      if (!assignment || !assignment.userId) return null;
      return (assignment.userId._id || assignment.userId).toString();
    };

    for (let i = 0; i < teamCount; i++) {
      const teamNumber = i + 1;
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(assignment => assignment.teamNumber === teamNumber)
        .map(assignment => ({
          userId: getUserIdString(assignment),
          username: assignment.username,
          inGameName: assignment.inGameName,
          preference: assignment.preference,
          isFiller: assignment.isFiller
        }));

      const isFull = teamMembers.length >= maxTeamSize;
      const isEmpty = teamMembers.length === 0;
      const hasSoloPlayer = teamMembers.some(m => m.preference === 'solo');

      // Check squad restrictions
      let hasSquadRestriction = false;

      for (const member of teamMembers) {
        if (member.userId === user?._id?.toString()) continue;
        if (member.preference !== 'squad') continue;

        const memberSquadData = (tournament.brTeamData || []).find(td => {
          const tdUserId = (td.userId?._id || td.userId)?.toString();
          return tdUserId === member.userId;
        });

        if (!memberSquadData || !memberSquadData.squadmates || memberSquadData.squadmates.length === 0) {
          hasSquadRestriction = true;
          break;
        }

        const currentUserName = (user?.inGameName || user?.username || '').toLowerCase().trim();

        const isAllowed = memberSquadData.squadmates.some(sm => {
          if (sm.isRandom === true) return true;
          if (sm.name && typeof sm.name === 'string') {
            const squadmateName = sm.name.toLowerCase().trim();
            if (squadmateName === '__random__') return false;
            return squadmateName === currentUserName;
          }
          return false;
        });

        if (!isAllowed) {
          hasSquadRestriction = true;
          break;
        }
      }

      // Team is available if not full, no solo player blocking, and no squad restrictions
      const isAvailable = !isFull && !hasSoloPlayer && !hasSquadRestriction;

      teamsArray.push({
        teamNumber,
        members: teamMembers,
        isFull,
        isEmpty,
        hasSoloPlayer,
        hasSquadRestriction,
        isAvailable,
        spotsRemaining: maxTeamSize - teamMembers.length
      });
    }

    return teamsArray;
  }, [tournament, user, isBRDuo, maxTeamSize]);

  // Determine what preference options are available
  const getAvailableOptions = () => {
    if (tournamentTeamMode === 'solo') {
      return ['solo'];
    }
    return ['solo', 'random', 'squad'];
  };

  const availableOptions = getAvailableOptions();

  // Update preference when tournament mode changes
  useEffect(() => {
    if (tournamentTeamMode === 'solo' && teamPreference !== 'solo') {
      setTeamPreference('solo');
    }
  }, [tournamentTeamMode, teamPreference]);

  const handleSquadmateChange = (index, value) => {
    const newSquadmates = [...squadmates];
    newSquadmates[index] = value;
    setSquadmates(newSquadmates);
  };

  const toggleSquadmateSlot = (index) => {
    const newSquadmates = [...squadmates];
    const currentValue = newSquadmates[index];

    if (currentValue === '') {
      newSquadmates[index] = '__RANDOM__';
    } else if (currentValue === '__RANDOM__') {
      newSquadmates[index] = '';
    } else {
      newSquadmates[index] = '__RANDOM__';
    }

    setSquadmates(newSquadmates);
  };

  const handleFinalJoin = (teamNum, preference = 'random') => {
    const teamData = {
      brTeamPreference: preference,
      brSquadmates: preference === 'squad' ? squadmates.filter(s => s !== '') : [],
      brTeamNumber: teamNum
    };

    onJoin(teamData);
  };

  const handleTeamSelect = (teamNumber) => {
    const team = teams.find(t => t.teamNumber === teamNumber);
    if (!team || !team.isAvailable) return;

    setSelectedTeamNumber(teamNumber);
    setSelectedTeamIsEmpty(team.isEmpty);

    // If team is empty AND tournament isn't solo-only, show preference selection
    // Otherwise, directly join (for non-empty teams)
    if (team.isEmpty && tournamentTeamMode !== 'solo') {
      setStep(2); // Go to preference selection for empty teams
    }
    // If solo tournament, select solo and stay on team selection (will join on button click)
    else if (tournamentTeamMode === 'solo') {
      setTeamPreference('solo');
    }
    // For non-empty teams, just select it (will join as random on button click)
    else {
      setTeamPreference('random'); // Default to random for non-empty teams
    }
  };

  const handleJoinSelectedTeam = () => {
    if (selectedTeamNumber === null) return;

    // If on step 2 (preference selection for empty team), use selected preference
    if (step === 2) {
      handleFinalJoin(selectedTeamNumber, teamPreference);
    } else {
      // On step 1 (team selection), join with appropriate preference
      handleFinalJoin(selectedTeamNumber, teamPreference);
    }
  };

  // Handle confirm from preference step (step 2)
  const handleConfirmPreference = () => {
    if (selectedTeamNumber !== null) {
      handleFinalJoin(selectedTeamNumber, teamPreference);
    }
  };

  // Get display info for tournament team mode
  const getTournamentModeInfo = () => {
    switch (tournamentTeamMode) {
      case 'solo':
        return {
          icon: '👤',
          label: t('soloTournament', 'Solo Tournament'),
          description: t('soloTournamentDesc', 'This tournament requires all players to play alone. No teammates allowed.')
        };
      case 'random':
        return {
          icon: '🎲',
          label: t('randomTournament', 'Open Team Tournament'),
          description: t('randomTournamentDesc', 'Any player can join and team up with others.')
        };
      case 'squad':
        return {
          icon: '👥',
          label: t('squadTournament', 'Squad Tournament'),
          description: t('squadTournamentDesc', 'The creator has specified squadmates. You can form your own squad or join as random.')
        };
      default:
        return {
          icon: '🎮',
          label: t('teamTournament', 'Team Tournament'),
          description: ''
        };
    }
  };

  const modeInfo = getTournamentModeInfo();

  // Available teams for selection
  const availableTeams = teams.filter(t => t.isAvailable);
  const hasAvailableTeams = availableTeams.length > 0;

  // Use portal to render modal at document body level to avoid z-index stacking context issues
  return ReactDOM.createPortal(
    <div className="br-modal-overlay">
      <div className="br-modal br-modal-wide">
        <div className="br-modal-header">
          <div className="br-modal-icon">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h3 className="br-modal-title">
            {step === 1
              ? t('selectTeam', 'Select Your Team')
              : t('teamPreferences', 'Team Preferences')}
          </h3>
          <button onClick={onClose} className="br-modal-close" disabled={loading}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="br-modal-content">
          {/* STEP 1: Team Selection (main step) */}
          {step === 1 && (
            <>
              {/* Tournament Team Mode Display */}
              <div className={`br-tournament-mode br-tournament-mode-${tournamentTeamMode}`}>
                <div className="br-tournament-mode-header">
                  <span className="br-tournament-mode-icon">{modeInfo.icon}</span>
                  <span className="br-tournament-mode-label">{modeInfo.label}</span>
                  {tournamentTeamMode === 'solo' && <Lock className="w-4 h-4 text-red-400 ml-2" />}
                </div>
                <p className="br-tournament-mode-desc">{modeInfo.description}</p>

                {tournamentTeamMode === 'squad' && creatorSquadmates.length > 0 && (
                  <div className="br-creator-squadmates">
                    <span className="br-creator-squadmates-label">
                      {t('creatorSquadmates', "Creator's Squadmates:")}
                    </span>
                    <div className="br-creator-squadmates-list">
                      {creatorSquadmates.map((sm, idx) => (
                        <span key={idx} className={`br-squadmate-tag ${sm.isRandom ? 'random' : 'specific'}`}>
                          {sm.isRandom ? '🎲 Random' : sm.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="br-modal-info">
                <AlertCircle className="w-5 h-5 text-blue-400" />
                <p>
                  {t('selectTeamToJoin', 'Select which team you want to join. Empty teams let you set team preferences.')}
                </p>
              </div>

              {!hasAvailableTeams && (
                <div className="br-no-teams-warning">
                  <AlertCircle className="w-5 h-5" />
                  <p>{t('noAvailableTeams', 'No available teams to join. All teams are full or have restrictions.')}</p>
                </div>
              )}

              <div className="br-teams-grid">
                {teams.map(team => (
                  <div
                    key={team.teamNumber}
                    className={`br-team-card ${
                      selectedTeamNumber === team.teamNumber ? 'selected' : ''
                    } ${!team.isAvailable ? 'disabled' : ''} ${team.isEmpty ? 'empty-team' : ''}`}
                    onClick={() => team.isAvailable && handleTeamSelect(team.teamNumber)}
                  >
                    <div className="br-team-card-header">
                      <div className="br-team-number">
                        <Shield className="w-4 h-4" />
                        {t('team', 'Team')} {String(team.teamNumber).padStart(2, '0')}
                      </div>
                      {selectedTeamNumber === team.teamNumber && (
                        <div className="br-team-selected-badge">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                      {team.isEmpty && team.isAvailable && (
                        <span className="br-team-empty-badge">
                          <UserPlus className="w-3 h-3" />
                          {t('captain', 'Captain')}
                        </span>
                      )}
                    </div>

                    <div className="br-team-members">
                      {team.members.map((member, idx) => (
                        <div key={idx} className={`br-team-member ${member.isFiller ? 'filler' : ''}`}>
                          <Users className="w-3 h-3" />
                          <span>{member.inGameName || member.username}</span>
                          {member.isFiller && <span className="br-member-badge filler">BOT</span>}
                          {member.preference === 'solo' && <span className="br-member-badge solo">SOLO</span>}
                          {member.preference === 'squad' && <span className="br-member-badge squad">SQUAD</span>}
                        </div>
                      ))}
                      {Array.from({ length: team.spotsRemaining }).map((_, idx) => (
                        <div key={`empty-${idx}`} className="br-team-member empty">
                          <Users className="w-3 h-3" />
                          <span>{t('emptySlot', 'Empty Slot')}</span>
                        </div>
                      ))}
                    </div>

                    {!team.isAvailable && (
                      <div className="br-team-unavailable">
                        {team.isFull && <span>❌ {t('teamFull', 'Team Full')}</span>}
                        {team.hasSoloPlayer && !team.isFull && <span>🔒 {t('soloPlayerTeam', 'Solo Player')}</span>}
                        {team.hasSquadRestriction && !team.isFull && !team.hasSoloPlayer && <span>🔒 {t('squadOnly', 'Squad Only')}</span>}
                      </div>
                    )}

                    {team.isAvailable && (
                      <div className="br-team-available">
                        {team.isEmpty ? (
                          <>✨ {t('createNewTeam', 'Create New Team')}</>
                        ) : (
                          <>✅ {t('available', 'Available')} ({team.spotsRemaining} {team.spotsRemaining === 1 ? t('spot', 'spot') : t('spots', 'spots')})</>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* STEP 2: Preference Selection (only for empty teams) */}
          {step === 2 && (
            <>
              <div className="br-captain-header">
                <div className="br-captain-icon">
                  <UserPlus className="w-6 h-6" />
                </div>
                <div className="br-captain-info">
                  <h4>{t('youAreCaptain', 'You are the Team Captain!')}</h4>
                  <p>{t('captainInfo', 'As team captain, you can set preferences for who can join your team.')}</p>
                </div>
              </div>

              <div className="br-team-selection">
                <label className="br-team-label">
                  {t('yourTeamPreference', 'Your Team Preference')}
                </label>

                {tournamentTeamMode === 'solo' ? (
                  <div className="br-locked-selection">
                    <div className="br-locked-option">
                      <Lock className="w-4 h-4" />
                      <span>{t('soloTeam', '👤 Solo - No teammates')}</span>
                    </div>
                    <p className="br-locked-reason">
                      {t('soloLockedReason', 'Tournament creator set this to Solo mode - all players must play alone.')}
                    </p>
                  </div>
                ) : (
                  <select
                    value={teamPreference}
                    onChange={(e) => {
                      setTeamPreference(e.target.value);
                      setSquadmates(['', '', '']);
                    }}
                    className="br-team-select"
                    disabled={loading}
                  >
                    {availableOptions.includes('solo') && (
                      <option value="solo">
                        {t('soloTeam', '👤 Solo - No teammates')}
                      </option>
                    )}
                    {availableOptions.includes('random') && (
                      <option value="random">
                        {t('randomTeam', '🎲 Random - Any player can join')}
                      </option>
                    )}
                    {availableOptions.includes('squad') && (
                      <option value="squad">
                        {t('squadTeam', '👥 Squad - Choose specific teammates')}
                      </option>
                    )}
                  </select>
                )}

                {teamPreference === 'squad' && tournamentTeamMode !== 'solo' && (
                  <div className="br-squad-selection">
                    <div className="br-squad-header">
                      <Users className="w-4 h-4" />
                      <span>
                        {t('selectSquadmates', 'Select Squadmates')} ({isBRDuo ? '1' : 'Up to 3'})
                      </span>
                    </div>

                    {Array.from({ length: maxSquadmates }).map((_, index) => {
                      const value = squadmates[index] || '';
                      const isRandom = value === '__RANDOM__';
                      const isEmpty = value === '';

                      return (
                        <div key={index} className="br-squadmate-slot">
                          <div className="br-squadmate-input-group">
                            <input
                              type="text"
                              value={isRandom ? '' : value}
                              onChange={(e) => handleSquadmateChange(index, e.target.value)}
                              placeholder={
                                isRandom ? t('randomPlayer', '🎲 Random Player') :
                                isEmpty ? t('emptySlot', 'Empty Slot (Optional)') :
                                t('playerName', 'Player Name')
                              }
                              className={`br-squadmate-input ${isRandom ? 'random' : isEmpty ? 'empty' : ''}`}
                              disabled={loading || isRandom}
                            />
                            <button
                              type="button"
                              onClick={() => toggleSquadmateSlot(index)}
                              className="br-squadmate-toggle"
                              disabled={loading}
                            >
                              {isEmpty ? '🎲' : isRandom ? '❌' : '🎲'}
                            </button>
                          </div>
                          <div className="br-squadmate-status">
                            {isEmpty && <span className="status-empty">⚪ {t('optional', 'Optional')}</span>}
                            {isRandom && <span className="status-random">🎲 {t('randomPlayer', 'Random Player')}</span>}
                            {!isEmpty && !isRandom && <span className="status-specific">✅ {t('specific', 'Specific Player')}</span>}
                          </div>
                        </div>
                      );
                    })}

                    <div className="br-squad-help">
                      💡 {t('squadHelpText', 'Toggle between specific player name, random player, or empty slot')}
                    </div>
                  </div>
                )}

                {teamPreference === 'solo' && tournamentTeamMode !== 'solo' && (
                  <div className="br-info-box solo">
                    👤 {t('soloInfo', 'You will play alone. No one can join your team.')}
                  </div>
                )}

                {teamPreference === 'random' && (
                  <div className="br-info-box random">
                    🎲 {t('randomInfo', 'Any available player can join your team.')}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="br-modal-actions">
          {step === 1 ? (
            <>
              <button
                onClick={handleJoinSelectedTeam}
                disabled={loading || selectedTeamNumber === null}
                className="br-btn br-btn-join"
              >
                {loading ? t('joining', 'Joining...') : (
                  selectedTeamIsEmpty && tournamentTeamMode !== 'solo'
                    ? t('setPreferencesFirst', 'Select Team First')
                    : t('joinSelectedTeam', 'Join Selected Team')
                )}
              </button>
              <button
                onClick={onClose}
                disabled={loading}
                className="br-btn br-btn-cancel"
              >
                {t('cancel', 'Cancel')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedTeamNumber(null);
                  setSelectedTeamIsEmpty(false);
                }}
                disabled={loading}
                className="br-btn br-btn-back"
              >
                <ChevronLeft className="w-4 h-4" /> {t('back', 'Back')}
              </button>
              <button
                onClick={handleConfirmPreference}
                disabled={loading}
                className="br-btn br-btn-join"
              >
                {loading ? t('joining', 'Joining...') : t('createTeam', 'Create Team')}
              </button>
              <button
                onClick={onClose}
                disabled={loading}
                className="br-btn br-btn-cancel"
              >
                {t('cancel', 'Cancel')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BRTeamJoinModal;
